import { createStore } from 'vuex';
// Assuming api.js exports both helpers
import { callProtectedApi } from '../api'; // Keep this if used directly elsewhere
// Import the service we created
import authService from '@/services/authService'; // Adjust path as needed

export default createStore({
  state: {
    // --- Existing State ---
    message: '', // General success messages
    errorMessage: '', // General error messages
    user: null, // Holds logged-in user data { id, email, fullName, ... }

    // --- Recommended Additions ---
    isLoading: false, // Global loading indicator state
    token: localStorage.getItem('jwt') || null, // Store token in state too (optional, but useful)

  },
  mutations: {
    // --- Existing Mutations ---
    LOGIN(state, { token, user }) { // Updated LOGIN to accept user data
      state.token = token;
      state.user = user;
      state.isLoading = false;
      state.errorMessage = '';
      state.message = 'Login successful!'; // Or set a more specific message if needed
    },
    LOGOUT(state) {
      state.token = null;
      state.user = null;
      state.message = '';
      state.errorMessage = '';
      state.isLoading = false;
      localStorage.removeItem('jwt'); // Ensure removal on logout mutation
    },
    SET_ERROR(state, message) {
      state.errorMessage = message;
      state.isLoading = false; // Stop loading on error
    },
    SET_USER_DATA(state, userData) {
      state.user = userData;
      state.isLoading = false; // Stop loading after fetching user data
    },

    // --- Recommended Additions ---
    SET_LOADING(state, loadingStatus) {
      state.isLoading = loadingStatus;
      if (loadingStatus) {
        state.errorMessage = ''; // Clear errors when starting to load
        state.message = ''; // Clear messages when starting to load
      }
    },
    CLEAR_ERROR(state) {
      state.errorMessage = '';
    },
    CLEAR_MESSAGE(state) {
        state.message = '';
    },
    SET_TOKEN(state, token) { // Mutation to specifically set token
        state.token = token;
        localStorage.setItem('jwt', token);
    }
  },
  actions: {
    // --- Login Action (Modified) ---
    async login({ commit }, credentials) {
      commit('SET_LOADING', true);
      try {
        const response = await authService.login(credentials);
        const { token, user } = response.data; // Adjust based on your API response

        if (!token || !user) {
           throw new Error("Invalid response received from server during login.");
        }

        commit('SET_TOKEN', token); // Save token to state & localStorage
        commit('LOGIN', { token, user }); // Update state (sets user, clears error, etc.)

        // No need to call fetchUserData here if login response includes user data
        return true; // Indicate success

      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Login failed.';
        // Check for specific verification error code from backend
        if (error.response?.data?.code === 'ACCOUNT_NOT_VERIFIED') {
            commit('SET_ERROR', message); // Set the specific error
             // Optionally return a specific value or let the component handle the re-throw
        } else {
            commit('SET_ERROR', message);
        }
        console.error("Vuex Login Action Error:", error);
        throw error; // Re-throw error for component to catch
      } finally {
           commit('SET_LOADING', false); // Ensure loading is always stopped
      }
    },

    // --- Logout Action (Modified slightly) ---
    logout({ commit }) {
      // Optional: Call a backend logout endpoint if you have one
      // try { await authService.logout(); } catch (e) { console.error(e); }
      commit('LOGOUT'); // Clears state and removes token from localStorage
       // Redirect happens in the component or route guard usually
    },

    // --- Register Action (New) ---
    async register({ commit }, userData) {
        commit('SET_LOADING', true);
        try {
            // authService handles the API call
            const response = await authService.register(userData);
             // Typically, registration doesn't log the user in automatically
             // or return a token. It often returns a success message.
            commit('CLEAR_MESSAGE'); // Clear previous messages
             // You might commit a general success message or let the component handle it
            // commit('SET_MESSAGE', response.data.message || 'Registration successful!');
            return response; // Return the full response for the component
        } catch (error) {
             const message = error.response?.data?.message || 'Registration failed.';
             commit('SET_ERROR', message);
             console.error("Vuex Register Action Error:", error);
             throw error; // Re-throw for component
        } finally {
             commit('SET_LOADING', false);
        }
    },

     // --- Forgot Password Action (New) ---
    async forgotPassword({ commit }, emailData) {
        commit('SET_LOADING', true);
        try {
            const response = await authService.forgotPassword(emailData);
            // Usually just returns a success message
            return response; // Return response for component
        } catch (error) {
             const message = error.response?.data?.message || 'Forgot password request failed.';
             commit('SET_ERROR', message); // May want to avoid specific errors here for security
             console.error("Vuex Forgot Password Action Error:", error);
             // Decide if you want to re-throw or just let component show generic success
             throw error;
        } finally {
             commit('SET_LOADING', false);
        }
    },

     // --- Reset Password Action (New) ---
    async resetPassword({ commit }, { token, passwordData }) {
        commit('SET_LOADING', true);
        try {
            const response = await authService.resetPassword(token, passwordData);
            // Usually just returns a success message
             return response; // Return response for component
        } catch (error) {
             const message = error.response?.data?.message || 'Password reset failed.';
             commit('SET_ERROR', message);
             console.error("Vuex Reset Password Action Error:", error);
             throw error; // Re-throw for component
        } finally {
             commit('SET_LOADING', false);
        }
    },

     // --- Resend Verification Action (New) ---
    async resendVerification({ commit }, emailData) {
        commit('SET_LOADING', true);
        try {
            const response = await authService.resendVerification(emailData);
            return response; // Return response for component
        } catch (error) {
             const message = error.response?.data?.message || 'Failed to resend verification email.';
             commit('SET_ERROR', message);
             console.error("Vuex Resend Verification Action Error:", error);
             throw error; // Re-throw for component
        } finally {
             commit('SET_LOADING', false);
        }
    },

    // --- Fetch User Data Action (Existing - slight modification) ---
    async fetchUserData({ commit }) {
        // Removed loading set here, assume it's set by caller if needed
        commit('CLEAR_ERROR'); // Clear previous errors before fetching
      try {
         // Use callProtectedApi as before, assuming it's correctly set up for auth headers
        const response = await callProtectedApi('/auth/user-data');
        commit('SET_USER_DATA', response.data); // Commit fetched data
        return response.data; // Return data
      } catch (error) {
        console.error('Error fetching user data:', error);
        const message = error.response?.data?.message || 'Failed to load user data.';
        commit('SET_ERROR', message);
        // Optional: Logout user if fetching data fails critically after login?
        // commit('LOGOUT');
        throw error; // Re-throw
      }
      // Removed finally block setting loading false, handled by caller or SET_USER_DATA/SET_ERROR
    },

    // --- Handle Google Auth Callback Action (New - Recommended) ---
    async handleAuthCallback({ commit, dispatch }, token) {
        commit('SET_LOADING', true);
        try {
            if (!token) throw new Error("Authentication token missing.");

             commit('SET_TOKEN', token); // Save token to state & localStorage

            // Fetch user data using the new token
            const userData = await dispatch('fetchUserData'); // Call fetchUserData action

            if (!userData) throw new Error("Failed to fetch user details after authentication.");

            // Use LOGIN mutation which now accepts user data
            commit('LOGIN', { token, user: userData });

            return true; // Indicate success

        } catch (error) {
             const message = error.message || 'Authentication callback failed.';
             commit('SET_ERROR', message);
             commit('LOGOUT'); // Log out if callback fails
             console.error("Vuex Auth Callback Action Error:", error);
             return false; // Indicate failure
        } finally {
             commit('SET_LOADING', false);
        }
    },

    // --- Action to try loading user data on app startup (New - Recommended) ---
     async tryAutoLogin({ commit, dispatch, state }) {
        if (state.token && !state.user) { // Only run if token exists but no user data yet
            console.log('Attempting auto-login with existing token...');
            commit('SET_LOADING', true);
             try {
                // Use fetchUserData which uses the protected API call (interceptor adds token)
                 await dispatch('fetchUserData');
            } catch (error) {
                 console.warn('Auto-login failed:', error.message);
                 // Token might be expired or invalid, log out
                 commit('LOGOUT');
            } finally {
                 commit('SET_LOADING', false);
            }
        }
    }

  },
  getters: {
    // --- Existing Getter ---
    hasRole: (state) => (...roles) => {
        // Ensure roles is always an array and user/role exist
        if (!state.user || !state.user.role) return false;
        const userRoles = Array.isArray(state.user.role) ? state.user.role : [state.user.role];
        const requiredRoles = Array.isArray(roles) ? roles : [roles];
        return requiredRoles.some(role => userRoles.includes(role));
    },

    // --- Recommended Additions ---
    isAuthenticated: (state) => !!state.user && !!state.token, // Check if user data and token exist
    getUser: (state) => state.user,
    isLoading: (state) => state.isLoading,
    getErrorMessage: (state) => state.errorMessage,
    getSuccessMessage: (state) => state.message,

  },
});